/** Vincular la cuenta de Telegram de la persona para recibir los avisos en el móvil. */
import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Group, Paper, Stack, Text } from '@mantine/core';
import { IconBrandTelegram } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { avisarError, avisarOk } from '../comun';
import { CodigoQr } from './DobleFactor';
import type { EstadoTelegram } from '../tipos';

export function TelegramVinculo() {
  const qc = useQueryClient();
  const [enlace, setEnlace] = useState<string | null>(null);
  // Mientras se espera a que la persona pulse «Iniciar» en Telegram, se consulta cada 3 s.
  const estado = useQuery({
    queryKey: ['telegram'],
    queryFn: () => api.get<EstadoTelegram>('/telegram/estado'),
    refetchInterval: (q) => (enlace && !q.state.data?.vinculado ? 3000 : false),
  });
  const e = estado.data;
  useEffect(() => {
    if (enlace && e?.vinculado) {
      setEnlace(null);
      avisarOk('Telegram vinculado', 'Listo');
    }
  }, [enlace, e?.vinculado]);

  const vincular = async () => {
    try {
      const r = await api.post<{ enlace: string }>('/telegram/vincular');
      setEnlace(r.enlace);
    } catch (err) {
      avisarError(err);
    }
  };
  const accion = async (ruta: string, ok: string) => {
    try {
      await api.post(ruta);
      avisarOk(ok);
      setEnlace(null);
      await qc.invalidateQueries({ queryKey: ['telegram'] });
    } catch (err) {
      avisarError(err);
    }
  };

  return (
    <Paper withBorder p="md" maw={560}>
      <Group justify="space-between" mb="xs">
        <Text fw={600}>Avisos por Telegram</Text>
        {e?.vinculado ? <Badge color="teal">Vinculado{e.nombre ? ` (${e.nombre})` : ''}</Badge> : <Badge color="gray">Sin vincular</Badge>}
      </Group>
      {e && !e.configurado ? (
        <Alert color="yellow">
          El servidor aún no tiene el bot de Telegram configurado (falta <code>TELEGRAM_BOT_TOKEN</code>). Se hace una vez con <code>infra/vps/configurar-telegram.sh</code>; mira app/README.md.
        </Alert>
      ) : e?.vinculado ? (
        <Stack gap="xs">
          <Text size="sm" c="dimmed">
            Recibirás aquí los avisos que tengan Telegram activado en <b>Ajustes › Avisos</b>. En el chat también puedes escribir <code>/avisos</code> para ver los pendientes.
          </Text>
          <Group>
            <Button variant="light" onClick={() => void accion('/telegram/probar', 'Mensaje de prueba enviado')}>
              Enviar un mensaje de prueba
            </Button>
            <Button variant="subtle" color="red" onClick={() => void accion('/telegram/desvincular', 'Telegram desvinculado')}>
              Desvincular
            </Button>
          </Group>
        </Stack>
      ) : enlace ? (
        <Stack gap="xs">
          <Text size="sm">
            Pulsa el botón (o escanea el código con el móvil) y, en Telegram, pulsa <b>Iniciar</b>. El enlace caduca en 10 minutos.
          </Text>
          <Group align="center" wrap="wrap">
            <Button component="a" href={enlace} target="_blank" rel="noopener noreferrer" leftSection={<IconBrandTelegram size={16} />}>
              Abrir Telegram
            </Button>
            <CodigoQr contenido={enlace} tamano={140} />
          </Group>
          <Text size="xs" c="dimmed">
            Esperando a que lo confirmes en Telegram…
          </Text>
        </Stack>
      ) : (
        <Stack gap="xs">
          <Text size="sm" c="dimmed">
            Recibe los plazos fiscales, vencimientos y alertas en tu móvil sin depender del correo. Cada persona vincula su propia cuenta.
          </Text>
          <Group>
            <Button leftSection={<IconBrandTelegram size={16} />} onClick={vincular} disabled={!e}>
              Vincular Telegram
            </Button>
          </Group>
        </Stack>
      )}
    </Paper>
  );
}
